/* eslint-disable @typescript-eslint/no-inferrable-types */
import {
  IBaseConfigurationFormModel,
  ICustomIdInputFieldSettingsModel,
  IGenericDataFetchFieldsConfigFormModel,
  IGenericLabelValueFieldsConfigFormModel,
  ILocationWidgetConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../../../models/basic-label-value-pair.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  LOCATION_WIDGET_LEVELS,
  LOCATION_COUNTRY_KEY,
  alphaNanoIdUtil,
  ICascadingConfig,
  EIremboFormlyFormStateKeys,
} from '@irembo-andela/irembogov3-common';
import { FormBuilderService } from '../../../services/form-builder.service';
import { ICustomIdFieldPopulateFieldItem } from '../../../models/custom-id-input-field-populates-item.model';
import { filter, tap } from 'rxjs';
import * as LocationWidgetUtils from '../location-widget-components-generator.util';
import { AbstractControl } from '@angular/forms';
import { TFormlyFieldKey } from '../../../models/formly-key.type';
import {
  LOCATION_WIDGET_CITY_LABEL,
  LOCATION_WIDGET_COUNTRY_LABEL,
  LOCATION_WIDGET_LOCAL_LABEL,
} from '../../../config/location-widget-field-labels.config';
import { GetEnumToLabelAndValueOptions } from '../../form-builder-field-comparator-dropdown-options.utils';
import {
  EFormBuilderDisplayConditionComparator,
  FormBuilderFieldsWithDataSet,
  FormBuilderFieldsWithOptions,
} from '../../../../core/models/form-builder-display-comparator.enum';

import { ServiceComparatorOptions } from '../../../../core/constants/comparator-options.constants';
import {
  buildComparatorOptions,
  fetchedDataFieldsFormFieldLabelAndDetails,
  getCurrentServiceFormFields,
} from '../conditional-settings-configurations';

export const getFieldLabelSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  const availableLabels: (string | null)[] = formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(false, true, [], false)
    .map(
      (field: IBasicFieldLabelAndDetails<TFormlyFieldKey>) =>
        field.label?.toLowerCase() ?? null
    );
  return [
    {
      key: 'fieldLabel',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Field Label',
        hideBuilderWrapper: true,
        required: true,
        placeholder: 'Field Label',
      },
      id: 'fieldLabel',
      validation: {
        messages: {
          required: 'Field Label is required',
        },
      },
      validators: {
        duplicateLabel: {
          expression: (c: AbstractControl) => {
            if (!c.value) return null;
            let noMatchedLabel = true;
            [
              c.value,
              `${c.value}: ${LOCATION_WIDGET_COUNTRY_LABEL}`.toLowerCase(),
              `${c.value}: ${LOCATION_WIDGET_LOCAL_LABEL}`.toLowerCase(),
              `${c.value}: ${LOCATION_WIDGET_CITY_LABEL}`.toLowerCase(),
            ].some((value: string) => {
              if (value && availableLabels.includes(value.toLowerCase())) {
                noMatchedLabel = false;
                return true;
              }
              return false;
            });
            return noMatchedLabel;
          },
          message: (error: any, field: FormlyFieldConfig) =>
            `"${field.formControl?.value}" is already used by another field.`,
        },
      },
    },
  ];
};

export const getFieldKeySettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'fieldKey',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Field Key',
        hideBuilderWrapper: true,
        required: false,
        placeholder: 'Field Key',
        description:
          'Unique identifier for this field. Auto-generated from label, but you can customize it.',
      },
      id: 'fieldKey',
      modelOptions: {
        updateOn: 'change',
      },
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          let userModifiedKey = false;

          // Generate initial key if empty
          const generateKeyFromLabel = () => {
            const currentLabel = field.parent?.model?.fieldLabel;
            if (currentLabel && currentLabel.trim() !== '') {
              const tempField: FormlyFieldConfig = {
                props: { label: currentLabel },
              };
              const updatedField = generateNewFieldKeyFromSettingsLabel(
                tempField,
                formBuilderServiceInstance
              );
              return updatedField.key as string;
            }
            return '';
          };

          // Set initial key if empty
          if (!field.model?.fieldKey || field.model.fieldKey.trim() === '') {
            const generatedKey = generateKeyFromLabel();
            if (generatedKey) {
              field.model.fieldKey = generatedKey;
            }
          }

          // Watch for manual changes to the key field
          if (field.formControl) {
            field.formControl.valueChanges.subscribe(() => {
              userModifiedKey = true;
            });
          }

          // Watch for label changes
          const labelField = field.parent?.fieldGroup?.find(
            f => f.key === 'fieldLabel'
          );
          if (labelField?.formControl) {
            labelField.formControl.valueChanges.subscribe(
              (newLabel: string) => {
                // Only auto-update if user hasn't manually modified the key
                if (!userModifiedKey && newLabel && newLabel.trim() !== '') {
                  const generatedKey = generateKeyFromLabel();
                  if (generatedKey && field.formControl) {
                    field.formControl.setValue(generatedKey, {
                      emitEvent: false,
                    });
                    field.model.fieldKey = generatedKey;
                  }
                }
              }
            );
          }
        },
      },
      validation: {
        messages: {
          duplicateKey: 'This key is already used by another field',
          validKey:
            'Field key must start with a letter and contain only letters and numbers (camelCase format).',
        },
      },
      validators: {
        duplicateKey: {
          expression: (c: AbstractControl, field: FormlyFieldConfig) => {
            if (!c.value || c.value.trim() === '') return true;

            const existingKeys = formBuilderServiceInstance
              .getAllFormFieldLabelAndDetails(false, true, [], false)
              .map(
                (fieldDetail: IBasicFieldLabelAndDetails<TFormlyFieldKey>) =>
                  fieldDetail.value as string
              )
              .filter((key: string) => key != null);

            return !existingKeys.includes(c.value.trim());
          },
          message: (error: any, field: FormlyFieldConfig) =>
            `"${field.formControl?.value}" is already used by another field.`,
        },
        validKey: {
          expression: (c: AbstractControl) => {
            if (!c.value || c.value.trim() === '') return true;
            return /^[a-zA-Z][a-zA-Z0-9]*$/.test(c.value.trim());
          },
          message: (error: any, field: FormlyFieldConfig) =>
            `Field key must start with a letter and contain only letters and numbers (camelCase format).`,
        },
      },
    },
  ];
};

export const getPlaceholderSettingsConfigFormly = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'placeholder',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Placeholder',
        disabled: false,
        required: false,
        placeholder: 'Placeholder',
      },
      id: 'placeholder',
      validation: {
        messages: {
          required: 'Placeholder is required',
        },
      },
    },
  ];
};

export const getDefaultBaseSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    ...getFieldLabelSettingsConfigFormly(formBuilderServiceInstance),
    ...getPlaceholderSettingsConfigFormly(),
    {
      key: 'tooltip',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Tooltip',
        disabled: false,
        required: false,
        placeholder: 'Tooltip',
      },
      id: 'tooltip',
    },
    {
      key: 'hint',
      type: 'input',
      props: {
        label: 'Hint',
        disabled: false,
        required: false,
        placeholder: 'Hint',
      },
      id: 'hint',
    },
    {
      key: 'hintPosition',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Hint Position',
        disabled: false,
        required: false,
        placeholder: 'Hint Position',
        multiple: false,
        bindValue: 'value',
        bindLabel: 'label',
        options: [
          {
            label: 'Top',
            value: 'TOP',
          },
          {
            label: 'Bottom',
            value: 'BOTTOM',
          },
        ],
      },
      id: 'hint',
    },
    {
      key: 'internalDescription',
      type: 'input',
      props: {
        label: 'Internal Description',
        disabled: false,
        required: false,
        placeholder: 'Internal Description',
      },
      id: 'internalDescription',
    },
    {
      key: 'readonly',
      type: EIremboFormlyFieldTypes.checkbox,
      props: {
        label: 'Read Only',
        hideHeader: true,
        placeholder: 'Read Only',
        required: false,
        formCheck: 'switch',
      },
      id: 'readonly',
    },
    ...getFieldKeySettingsConfigFormly(formBuilderServiceInstance),
  ];
};

export const baseFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IBaseConfigurationFormModel => {
  const model: IBaseConfigurationFormModel = {
    fieldKey: (field.key as string) ?? '',
    fieldLabel: field.props?.label ?? '',
    placeholder: field.props?.placeholder ?? '',
    hint: field.props?.['hint'] ?? '',
    hintPosition: field.props?.['hintPosition'] ?? undefined,
    tooltip: field.props?.['tooltip'] || '',
    internalDescription: field.props?.['internalDescription'] ?? '',
    readonly: field.props?.['readonly'] ?? false,
  };

  return model;
};

export const baseFieldUpdateFromSettingsFormModel = (
  model: IBaseConfigurationFormModel,
  baseField: FormlyFieldConfig
): FormlyFieldConfig => {
  const newFieldKeyRequired = model.fieldLabel != baseField.props?.['label'];

  if (baseField.props) {
    baseField.props['label'] = model.fieldLabel ?? undefined;
    baseField.props['hint'] = model.hint;
    baseField.props['hintPosition'] = model.hintPosition;
    baseField.props['placeholder'] = model.placeholder ?? undefined;
    baseField.props['tooltip'] = model.tooltip;
    baseField.props['internalDescription'] = model.internalDescription;
    baseField.props['readonly'] = model.readonly;
  } else {
    baseField['props'] = {
      label: model.fieldLabel ?? undefined,
      hint: model.hint,
      hintPosition: model.hintPosition,
      placeholder: model.placeholder ?? undefined,
      tooltip: model.tooltip,
      internalDescription: model.internalDescription,
      readonly: model.readonly,
    };
  }

  // Handle field key update
  if (model.fieldKey && model.fieldKey.trim() !== '') {
    // Store old key before updating to new manual key
    const oldKey = baseField.key;
    const newKey = model.fieldKey.trim();

    // Only set oldKey if the key is actually changing
    if (oldKey !== newKey) {
      if (!baseField.props) {
        baseField.props = {};
      }
      baseField.props['oldKey'] = oldKey;
      baseField.key = newKey;
    }
  } else if (newFieldKeyRequired || !baseField.key) {
    // Auto-generate key from label if no key provided or label changed
    baseField = generateNewFieldKeyFromSettingsLabel(baseField);
  }

  return baseField;
};

export const generateGenericLabelValueSettingsContentConfig = (
  repeaterFieldLabel: string = 'Field Option',
  withMultiple: boolean = false
): FormlyFieldConfig => {
  const config: FormlyFieldConfig = {
    key: 'contentSection',
    type: EIremboFormlyFieldTypes['formly-group'],
    className: 'settings_content_section_wrapper',
    props: {
      label: 'Content',
      placeholder: 'Content',
    },
    id: 'contentSection',
    wrappers: ['field-wrapper'],
  };

  config.fieldGroup = [
    {
      key: 'optionLabelValuePairs',
      type: EIremboFormlyFieldTypes.customlabelvaluerepeater,
      className: 'custom_label_value_repeater_field',
      props: {
        hideHeader: true,
        addText: 'Add',
        label: repeaterFieldLabel,
      },
      fieldArray: {
        type: EIremboFormlyFieldTypes['formly-group'],
        fieldGroup: [
          {
            key: 'label',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Label',
              placeholder: 'Label',
              required: true,
            },
          },
          {
            key: 'value',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Value',
              placeholder: 'Value',
              required: true,
            },
          },
        ],
      },
    },
  ];

  if (withMultiple) {
    config.fieldGroup.unshift({
      key: 'multiple',
      type: EIremboFormlyFieldTypes.radio,
      className: 'content_multiple_selector_radio_field',
      props: {
        label: '',
        placeholder: '',
        hideHeader: true,
        required: false,
        options: [
          { label: 'Single Selector', value: false },
          { label: 'Multiple Selector', value: true },
        ],
      },
      id: 'multiple',
    });
  }

  return config;
};

export const genericLabelValueFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IGenericLabelValueFieldsConfigFormModel => {
  let optionLabelValuePairs: IBasicLabelValuePair<string>[] = [];

  if (field.props?.['options']) {
    optionLabelValuePairs = Array.from(
      field.props?.['options'] as IBasicLabelValuePair<string>[]
    ).map(item => {
      return { value: item.value, label: item.label };
    });
  }

  const model: IGenericLabelValueFieldsConfigFormModel = {
    ...baseFieldParseIntoSettingsFormModel(field),
    contentSection: {
      optionLabelValuePairs,
    },
    readonly: field.props?.['readonly'] ?? false,
    disabled: field.props?.['disabled'] ?? false,
  };

  return model;
};

export const genericLabelValueFieldUpdateFromSettingsFormModel = (
  model: IGenericLabelValueFieldsConfigFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  let options: IBasicLabelValuePair<string | null>[] = [];

  if (model?.contentSection?.optionLabelValuePairs) {
    options = model.contentSection.optionLabelValuePairs.map(
      (valueItem: IBasicLabelValuePair<string | null>) => {
        return { value: valueItem.value, label: valueItem.label };
      }
    );
  }

  // Use base field update to handle common properties including fieldKey
  field = baseFieldUpdateFromSettingsFormModel(model, field);

  if (field.props) {
    field.props['options'] = options;
    field.props['disabled'] = model.disabled;
  } else {
    field['props'] = {
      options,
      disabled: model.disabled,
    };
  }

  return field;
};

export const generateNewFieldKeyFromSettingsLabel = (
  field: FormlyFieldConfig,
  formBuilderService?: FormBuilderService
): FormlyFieldConfig => {
  if (field?.props?.['label']) {
    // Generate proper camelCase key from label
    const baseKey = generateCamelCaseKey(field.props['label']);

    // Get existing keys to check for duplicates
    const existingKeys = getExistingFormKeys(field, formBuilderService);

    // Generate unique key with incremental numbering
    const newKey = generateUniqueKey(baseKey, existingKeys);

    field.props['oldKey'] = field.key;
    field.key = newKey;
  }
  return field;
};

/**
 * Converts a label string to proper camelCase
 * Example: "First Name" -> "firstName"
 */
const generateCamelCaseKey = (label: string): string => {
  return (
    label
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove non-alphanumeric except spaces
      .trim() || 'unNamedField'
  )
    .split(/\s+/) // Split on any whitespace
    .filter(word => word.length > 0) // Remove empty strings
    .map((word, index) => {
      // First word lowercase, subsequent words with first letter uppercase
      if (index === 0) {
        return word.charAt(0).toLowerCase() + word.slice(1).toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

/**
 * Extracts existing field keys from the form structure
 * Uses FormBuilderService when available, falls back to parent traversal
 */
const getExistingFormKeys = (
  field: FormlyFieldConfig,
  formBuilderService?: FormBuilderService
): string[] => {
  if (formBuilderService) {
    // Use FormBuilderService to get all field keys
    return formBuilderService
      .getAllFormFieldLabelAndDetails(false, true, [], false)
      .map(
        (fieldDetail: IBasicFieldLabelAndDetails<TFormlyFieldKey>) =>
          fieldDetail.value as string
      )
      .filter((key: string) => key != null);
  }

  // Fallback to parent traversal when FormBuilderService is not available
  const keys: string[] = [];

  // Try to get root form fields through parent chain
  let rootFieldGroup = field.parent;
  while (rootFieldGroup?.parent) {
    rootFieldGroup = rootFieldGroup.parent;
  }

  if (rootFieldGroup?.fieldGroup) {
    extractKeysFromFieldGroup(rootFieldGroup.fieldGroup, keys);
  } else if (field.parent?.fieldGroup) {
    // Fallback to parent fieldGroup if root not found
    extractKeysFromFieldGroup(field.parent.fieldGroup, keys);
  }

  return keys;
};

/**
 * Recursively extracts keys from a field group
 */
const extractKeysFromFieldGroup = (
  fieldGroup: FormlyFieldConfig[],
  keys: string[]
): void => {
  fieldGroup.forEach(field => {
    if (field.key && typeof field.key === 'string') {
      keys.push(field.key);
    }
    if (field.fieldGroup) {
      extractKeysFromFieldGroup(field.fieldGroup, keys);
    }
  });
};

/**
 * Generates a unique key by adding incremental numbers if duplicates exist
 * Example: "firstName" -> "firstName", "firstName1", "firstName2", etc.
 */
const generateUniqueKey = (baseKey: string, existingKeys: string[]): string => {
  if (!existingKeys.includes(baseKey)) {
    return baseKey;
  }

  let counter = 1;
  let candidateKey = `${baseKey}${counter}`;

  while (existingKeys.includes(candidateKey)) {
    counter++;
    candidateKey = `${baseKey}${counter}`;
  }

  return candidateKey;
};

export const getDateFieldSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => [
  ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
  {
    key: 'dateFormat',
    type: EIremboFormlyFieldTypes.customdropdown,
    props: {
      label: 'Date Format',
      placeholder: 'Select Date Format',
      multiple: false,
      bindValue: 'value',
      bindLabel: 'label',
      options: [
        {
          label: 'DD MM YYYY',
          value: 'DD MM YYYY',
        },
        {
          label: 'MM DD YYYY',
          value: 'MM DD YYYY',
        },
      ],
    },
  },
  {
    key: 'delimeter',
    type: EIremboFormlyFieldTypes.customdropdown,
    props: {
      label: 'Delimeter',
      placeholder: 'Select Date Delimeter',
      multiple: false,
      bindValue: 'value',
      bindLabel: 'label',
      options: [
        {
          label: '/',
          value: '/',
        },
        {
          label: '-',
          value: '-',
        },
      ],
    },
  },
  {
    key: 'eventCode',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Event Code',
      placeholder: 'Event code for date validation',
      description: 'Code for an event to fetch available dates',
    },
  },
  {
    key: 'eventDatesMaxSize',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Event Dates Max Size',
      placeholder: 'Maximum number of dates to fetch',
      description: 'Maximum number of dates to fetch for an event',
    },
    expressions: {
      'props.hideField': (field: FormlyFieldConfig) => {
        return !field.parent?.model?.['eventCode'];
      },
    },
  },
];

export const getPopulatesPropsSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService,
  dataFieldProps: string[]
): FormlyFieldConfig[] => {
  return [
    {
      key: 'populates',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      wrappers: ['custom-expansion-panel-with-icon'],
      props: {
        label: 'Populate Fields',
        hideLabel: true,
        required: false,
        titleFieldName: 'targetKey',
        addText: 'Add Fields to populate',
      },
      fieldArray: {
        fieldGroup: [
          {
            key: 'valueKey',
            type: dataFieldProps.length
              ? EIremboFormlyFieldTypes.customdropdown
              : EIremboFormlyFieldTypes.input,
            props: {
              options: dataFieldProps.map((prop: string) => {
                return { label: prop, value: prop };
              }),
              placeholder: 'Enter key of ID data field',
              bindLabel: 'label',
              bindValue: 'value',
              hideHeader: true,
            },
          },
          {
            key: 'targetKey',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              placeholder: 'Select target Field to populate data',
              options:
                formBuilderServiceInstance.getConfiguredFieldSiblingsFieldLabelAndDetails(
                  true,
                  true
                ),
              multiple: false,
              bindLabel: 'label',
              bindValue: 'value',
              required: false,
              hideHeader: true,
            },
            expressions: {
              hide: 'field?.parent?.parent?.parent?.model?.populateRelativeToRootForm',
            },
          },
          {
            key: 'targetKey',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              placeholder: 'Select target Field to populate data',
              options: formBuilderServiceInstance
                .getAllFormFieldLabelAndDetails(true, true)
                .map(fieldInfo => {
                  return {
                    label: fieldInfo.label,
                    value: `${fieldInfo.fieldRefPath}`.replace(/\?/g, ''),
                  };
                }),
              multiple: false,
              bindLabel: 'label',
              bindValue: 'value',
              required: false,
              hideHeader: true,
            },
            expressions: {
              hide: '!field?.parent?.parent?.parent?.model?.populateRelativeToRootForm',
            },
          },
        ],
      },
    },
  ];
};

// "Enable MixPanel Analytics" toggle group (model path: mixpanelEventAnalyticsConfig).
export const getMixpanelAnalyticsToggleConfig = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'mixpanelEventAnalyticsConfig',
      id: 'mixpanelEventAnalyticsConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: { dragDisabled: false },
      fieldGroup: [
        {
          key: 'mixpanelEventAnalytics',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: { label: 'Enable MixPanel Analytics', formCheck: 'switch' },
        },
      ],
    },
  ];
};

export const getUsePrefetchFieldConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  return {
    key: 'usePrefetch',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: {
      label: 'Prefetch value from user profile.',
    },
    expressions: {
      hide: (): boolean => {
        const configuredField: FormlyFieldConfig | null =
          formBuilderServiceInstance.configureFieldAction$?.value;
        return !(
          configuredField?.props?.['idType'] === 'NID' ||
          configuredField?.type === EIremboFormlyFieldTypes.customtininput
        );
      },
    },
  };
};

export const getPopulateRelativeToRootFieldConfig = (): FormlyFieldConfig => {
  return {
    key: 'populateRelativeToRootForm',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: {
      label: 'Enable populating of fields in all form sections',
      description:
        'On default, you can only populate sibling fields. Enable this to allow population of data into fields in other form sections.',
    },
  };
};

export const getPayloadKeySettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'useMultiplePayloadKeys',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        label: 'Use Multiple Payload Keys',
      },
    },
    {
      key: 'payloadKey',
      type: 'input',
      props: {
        label: 'Payload Key',
        required: true,
        placeholder: 'Enter Payload Key',
      },
      validation: {
        messages: {
          required: 'This field is required',
        },
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return field?.parent?.model?.useMultiplePayloadKeys === true;
        },
      },
    },
    {
      key: 'payloadKeyMappings',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      wrappers: ['custom-expansion-panel-with-icon'],
      props: {
        label: 'Payload Key Mappings',
        addText: 'Add Payload Key Mapping',
        titleFieldName: 'key',
      },
      fieldArray: {
        fieldGroup: [
          {
            key: 'key',
            type: 'input',
            props: {
              label: 'Payload Key',
              required: true,
              placeholder: 'Enter payload key name',
            },
            validation: {
              messages: {
                required: 'Payload key is required',
              },
            },
          },
          {
            key: 'isStatic',
            type: EIremboFormlyFieldTypes.checkbox,
            defaultValue: false,
            props: {
              label: 'Use Static Value',
              checked: false,
            },
            modelOptions: {
              updateOn: 'change',
            },
          },
          {
            key: 'staticValue',
            type: 'input',
            props: {
              label: 'Static Value',
              placeholder: 'Enter static value',
            },
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return field?.parent?.model?.isStatic !== true;
              },
              'props.required': (field: FormlyFieldConfig) => {
                return field?.parent?.model?.isStatic === true;
              },
            },
          },
          {
            key: 'sourceField',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              label: 'Source Field',
              placeholder: 'Select source field',
              options:
                formBuilderServiceInstance.getAllFormFieldLabelAndDetails(
                  true,
                  false
                ),
              multiple: false,
              bindLabel: 'label',
              bindValue: 'value',
              hideHeader: true,
            },
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return field?.parent?.model?.isStatic === true;
              },
              'props.required': (field: FormlyFieldConfig) => {
                return field?.parent?.model?.isStatic !== true;
              },
            },
          },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return field?.parent?.model?.useMultiplePayloadKeys !== true;
        },
      },
    },
  ];
};

export const populatesPropsUpdateFromSettingsFormModel = (
  model: ICustomIdInputFieldSettingsModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (field?.props?.['populates']) {
    delete field.props['populates'];
  }

  if (!model.populates || model.populates.length < 1) return field;

  const fieldProps = field?.props ? { ...field?.props } : {};

  fieldProps['populates'] = model.populates;
  fieldProps['populateRelativeToRootForm'] = model.populateRelativeToRootForm;

  field.props = { ...fieldProps };

  return field;
};

export const populatesPropsParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ICustomIdInputFieldSettingsModel => {
  const populateFields: ICustomIdFieldPopulateFieldItem[] = [];
  if (
    field?.props?.['populates'] &&
    Array.isArray(field?.props?.['populates'])
  ) {
    field.props['populates'].forEach(
      (populateItem: ICustomIdFieldPopulateFieldItem) => {
        populateFields.push(populateItem);
      }
    );
  }

  const model: ICustomIdInputFieldSettingsModel =
    baseFieldParseIntoSettingsFormModel(field);

  if (field?.props?.['idType'] === 'NID') {
    model.usePrefetch = field?.props?.['usePrefetch'] ? true : false;
  }

  return {
    ...model,
    populateRelativeToRootForm: field?.props?.['populateRelativeToRootForm']
      ? true
      : false,
    populates: populateFields,
  };
};

export const generateDataFetchDataSettingsConfig = (): FormlyFieldConfig => {
  const config: FormlyFieldConfig = {
    key: 'datasetSection',
    wrappers: ['panel-wrapper'],
    props: {
      label: 'Data',
      required: false,
    },
  };

  config.fieldGroup = [
    {
      key: 'code',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        useBaseUrl: true,
        label: 'Integration',
        placeholder: 'Integration',
        required: true,
        bindLabel: 'name',
        bindValue: 'code',
        options: [],
        dataset: {
          url: '/integration/v1/endpoints',
          params: {
            page: 0,
            size: 10,
            adapterCode: 'FFORWARD',
          },
          pagination: true,
          searching: true,
          dataField: 'data.content',
          searchField: 'name',
        },
      },
    },
    {
      key: 'url',
      type: EIremboFormlyFieldTypes.input,
      defaultValue: '/integration/v1/fetch/sync',
      props: {
        label: 'Url',
        hideHeader: true,
        placeholder: 'Url',
        required: true,
        disabled: true,
      },
      id: 'url',
      expressions: {
        hide: 'true',
      },
    },
    {
      key: 'dataField',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Data Field',
        placeholder: 'Data Field',
        required: false,
        disabled: false,
      },
      id: 'dataField',
    },
    {
      key: 'bindLabel',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Data Label',
        placeholder: 'Data Label',
        bindLabel: 'label',
        bindValue: 'value',
        required: true,
        options: [],
      },
      id: 'bindLabel',
    },
    {
      key: 'bindValue',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Data Value',
        placeholder: 'Data Value',
        bindLabel: 'label',
        bindValue: 'value',
        required: true,
        options: [],
      },
      id: 'bindValue',
    },
  ];

  return config;
};

export const generateDatasetSettingsConfig = (): FormlyFieldConfig => {
  const config: FormlyFieldConfig = {
    key: 'datasetSection',
    wrappers: ['panel-wrapper'],
    props: {
      label: 'Data',
      required: false,
    },
  };

  config.fieldGroup = [
    {
      key: 'code',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        label: 'Datasets',
        placeholder: 'Select dataset',
        bindLabel: 'name',
        virtualScroll: true,
        required: true,
        useBaseUrl: true,
        dataset: {
          url: `/admin/v1/datasets`,
          params: {
            page: 0,
            size: 10,
            sortDirection: 'DESC',
            sort: 'createdDate',
          },
          pagination: true,
          searching: true,
          dataField: 'data.content',
          searchField: 'name',
        },
      },
    },
    {
      key: 'bindLabel',
      type: EIremboFormlyFieldTypes.input,
      defaultValue: 'label',
      props: {
        label: 'Data Label',
        hideHeader: true,
        placeholder: 'Data Label',
        disabled: true,
      },
      id: 'bindLabel',
      expressions: {
        hide: 'true',
      },
    },
    {
      key: 'bindValue',
      type: EIremboFormlyFieldTypes.input,
      defaultValue: '',
      props: {
        label: 'Data Value',
        hideHeader: true,
        placeholder: 'Data Value',
        disabled: true,
      },
      id: 'bindValue',
      expressions: {
        hide: 'true',
      },
    },
  ];
  return config;
};

export const generateNonPaginatedDataSetSettingsConfig =
  (): FormlyFieldConfig => {
    const config: FormlyFieldConfig = {
      key: 'datasetSection',
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Data',
        required: false,
      },
    };

    config.fieldGroup = [
      {
        key: 'code',
        type: EIremboFormlyFieldTypes.customdropdownpaginated,
        props: {
          label: 'Datasets',
          placeholder: 'Select dataset',
          bindLabel: 'name',
          virtualScroll: true,
          required: true,
          useBaseUrl: true,
          dataset: {
            url: `/admin/v1/datasets`,
            params: {
              page: 0,
              size: 10,
              sortDirection: 'DESC',
              sort: 'createdDate',
            },
            pagination: true,
            searching: true,
            dataField: 'data.content',
            searchField: 'name',
          },
        },
      },
      {
        key: 'bindLabel',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: 'label',
        props: {
          label: 'Data Label',
          hideHeader: true,
          placeholder: 'Data Label',
          disabled: true,
        },
        id: 'bindLabel',
        expressions: {
          hide: 'true',
        },
      },
      {
        key: 'bindValue',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: '',
        props: {
          label: 'Data Value',
          hideHeader: true,
          placeholder: 'Data Value',
          disabled: true,
        },
        id: 'bindValue',
        expressions: {
          hide: 'true',
        },
      },
    ];
    return config;
  };

export const genericDataFieldFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IGenericDataFetchFieldsConfigFormModel => {
  const dataset = field.props?.['dataset'] as any;
  const datasetParams = dataset?.['params'] as any;
  const datasetUrl = dataset?.['url'];
  const datasetCodeFromUrl =
    typeof datasetUrl === 'string'
      ? datasetUrl.match(/\/by-dataset-code\/([^/?#]+)/)?.[1]
      : undefined;
  const dataSetSection = {
    url: datasetUrl,
    pagination: dataset?.['pagination'],
    searching: dataset?.['searching'],
    dataField: dataset?.['dataField'],
    searchField: dataset?.['searchField'] ?? field.props?.['bindLabel'],
    bindValue: field.props?.['bindValue'],
    bindLabel: field.props?.['bindLabel'],
    params: datasetParams,
    code:
      field.props?.['code'] ??
      datasetParams?.['datasetId'] ??
      (datasetCodeFromUrl ? decodeURIComponent(datasetCodeFromUrl) : undefined),
  };
  const model = {
    fieldLabel: field.props?.label ?? '',
    placeholder: field.props?.placeholder ?? '',
    hint: field.props?.['hint'] ?? '',
    tooltip: field.props?.['tooltip'] ?? '',
    internalDescription: field.props?.['internalDescription'] ?? '',
    options: [],
    datasetSection: dataSetSection,
    bindLabel: field.props?.['bindLabel'] ?? 'label',
    bindValue: field.props?.['bindValue'] ?? '',
    readonly: field.props?.['readonly'] ?? false,
    disabled: field.props?.['disabled'] ?? false,
  };
  return model;
};

export const genericDataFieldUpdateFromSettingsFormModel = (
  model: IGenericDataFetchFieldsConfigFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  const params: Record<string, any> = {};
  const dataset: Record<string, any> = {};

  switch (field?.props?.['subType']) {
    case EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET:
      dataset[
        'url'
      ] = `/admin/v1/dataset-items/by-dataset-code/${model.datasetSection?.code}`;
      dataset['dataField'] = 'data';
      break;
    case EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED:
      params['datasetId'] = model.datasetSection?.code;
      dataset['dataField'] = 'data.content';
      dataset['url'] = '/admin/v1/dataset-items';
      dataset['pagination'] = true;
      dataset['searching'] = true;
      params['page'] = 0;
      params['size'] = 10;
      dataset['params'] = params;
      break;
    case EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH:
      dataset['httpMethod'] = 'POST';
      dataset['dataField'] = model.datasetSection?.dataField ?? '';
      dataset['url'] = model.datasetSection?.url;
      break;
    default:
      dataset['dataField'] = 'data.content';
      dataset['url'] = model.datasetSection?.url;
  }

  if (field.props) {
    field.props['label'] = model.fieldLabel ?? undefined;
    field.props['placeholder'] = model.placeholder ?? undefined;
    field.props['hint'] = model.hint;
    field.props['tooltip'] = model.tooltip;
    field.props['internalDescription'] = model.internalDescription;
    field.props['options'] = model.options;
    field.props['readonly'] = model.readonly;
    field.props['disabled'] = model.disabled;
    field.props['bindLabel'] = model.datasetSection?.bindLabel ?? 'label';
    field.props['bindValue'] = model.datasetSection?.bindValue ?? '';
    field.props['dataset'] = dataset;
    field.props['code'] = model.datasetSection?.code ?? undefined;
  } else {
    field['props'] = {
      label: model.fieldLabel ?? undefined,
      placeholder: model.placeholder ?? undefined,
      hint: model.hint,
      internalDescription: model.internalDescription,
      options: model.options,
      tooltip: model.tooltip,
      bindLabel: model.datasetSection.bindLabel ?? 'label',
      bindValue: model.datasetSection.bindValue ?? '',
      dataset: dataset,
      readonly: model.readonly,
      disabled: model.disabled,
      internal: model.internal,
      code: model.datasetSection.code ?? undefined,
    };
  }
  field = generateNewFieldKeyFromSettingsLabel(field);
  return field;
};

export const getLocationWidgetFieldSettingsConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  const options: any = {};
  return [
    {
      key: 'locationFieldContentConfig',
      id: 'locationFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      fieldGroupClassName: 'location-widget-formly-group',
      props: {
        label: 'Content',
        dragDisabled: true,
      },
      fieldGroup: [
        {
          key: 'locationFirstLevel',
          type: EIremboFormlyFieldTypes.customdropdown,
          defaultValue: Object.keys(LOCATION_WIDGET_LEVELS).shift(),
          props: {
            options: getLocationFieldLevelFieldOptions(true),
            label: 'Location First Level',
            placeholder: 'Select location first level',
            bindLabel: 'label',
            bindValue: 'value',
            required: true,
          },
          hooks: {
            onInit: field => {
              return field?.options?.fieldChanges?.pipe(
                filter(e => {
                  return e.type === 'valueChanges' && e.field.key === field.key;
                }),
                tap(() => {
                  const firstLevel = field.formControl?.value;
                  if (
                    firstLevel === LOCATION_COUNTRY_KEY ||
                    !validateLocationFieldLevelFieldValuesIndex(
                      firstLevel,
                      field.parent?.model?.['locationLastLevel']
                    )
                  ) {
                    field.parent?.formControl
                      ?.get('locationLastLevel')
                      ?.setValue(undefined);
                  }
                })
              );
            },
          },
        },
        {
          key: 'locationLastLevel',
          type: EIremboFormlyFieldTypes.customdropdown,
          className: 'location-last-level-formly-group',
          props: {
            options: [],
            label: 'Location last Level',
            placeholder: 'Select location last level',
            internalDescriptionasHtml: true,
            bindLabel: 'label',
            bindValue: 'value',
            required: false,
          },
          expressions: {
            'props.options': (field: FormlyFieldConfig) => {
              const levels = getLocationFieldLevelFieldOptions(false);
              const firstLevel = field.parent?.model['locationFirstLevel'];
              if (firstLevel === LOCATION_COUNTRY_KEY) return levels;
              const firstLevelIndex = levels.findIndex(
                option => option.value === firstLevel
              );
              return firstLevelIndex >= 0
                ? levels.slice(firstLevelIndex + 1)
                : [];
            },
            hide: (field: FormlyFieldConfig) => {
              const availableOptions = field.props?.options;
              return (
                !Array.isArray(availableOptions) ||
                availableOptions.length === 0
              );
            },
            'props.required': (field: FormlyFieldConfig) => {
              return (
                !field.hide &&
                field.parent?.model?.['locationFirstLevel'] !==
                  LOCATION_COUNTRY_KEY
              );
            },
            'props.internalDescription': (field: FormlyFieldConfig) => {
              if (
                validateLocationFieldLevelFieldValuesIndex(
                  field.parent?.model?.['locationFirstLevel'],
                  field.formControl?.value
                )
              ) {
                const treeList: string[] = [
                  LOCATION_COUNTRY_KEY,
                  ...Object.keys(LOCATION_WIDGET_LEVELS),
                ];
                const firstLevelIndex = treeList.indexOf(
                  field.parent?.model?.['locationFirstLevel'] ??
                    LOCATION_COUNTRY_KEY
                );
                const selectedLastLevel =
                  field.parent?.model?.['locationLastLevel'];
                const lastLevelIndex = selectedLastLevel
                  ? treeList.indexOf(selectedLastLevel)
                  : firstLevelIndex;
                const neededLevels: string[] = treeList.filter(
                  (_key: string, index: number) => {
                    return index >= firstLevelIndex && index <= lastLevelIndex;
                  }
                );
                return neededLevels
                  .map((level: string, index: number) => {
                    return Array(index).fill('-').join('') + level;
                  })
                  .join('<br/>');
              }
              return null;
            },
          },
        },
        {
          key: 'international',
          type: EIremboFormlyFieldTypes['formly-group'],
          fieldGroupClassName: 'international-section-formly-group',
          props: {
            label: 'International',
            dragDisabled: true,
          },
          fieldGroup: [
            {
              key: 'enableInternationalCity',
              id: 'enableInternationalCity',
              type: EIremboFormlyFieldTypes.checkbox,
              defaultValue: false,
              className: 'international-city-formly-group',
              props: {
                label: 'City',
                placeholder: 'City',
                required: false,
              },
            },
          ],
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return (
                field?.parent?.model?.['locationFirstLevel']?.toLowerCase() !==
                'country'
              );
            },
          },
        },
        {
          key: 'excludedItemsConfiguration',
          type: EIremboFormlyFieldTypes['formly-group'],
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Exclude Value(s)',
            dragDisabled: true,
            collapsible: true,
            collapsed: true,
          },
          fieldGroup: generateDatasetExcludeConfigurationFields(),
          expressions: {},
        },
        {
          key: 'defaultValues',
          type: EIremboFormlyFieldTypes['formly-group'],
          wrappers: ['panel-wrapper'],
          className: 'mt-3',
          props: {
            label: 'Default Value(s)',
            dragDisabled: true,
            collapsible: true,
            collapsed: true,
          },
          fieldGroup: generateDefaultValueConfigurationFields(),
          hooks: {
            onInit: field => {
              return field?.options?.fieldChanges?.pipe(
                filter(e => {
                  return (
                    e.type === 'valueChanges' &&
                    (e.field.key === 'locationFirstLevel' ||
                      e.field.key === 'locationLastLevel')
                  );
                }),
                tap(() => {
                  field?.parent?.formControl
                    ?.get('defaultValues')
                    ?.get('locationWidget')
                    ?.setValue(null);
                })
              );
            },
          },
        },
        {
          key: 'conditionalLevelSettings',
          id: 'conditionalLevelSettings',
          type: EIremboFormlyFieldTypes['formly-group'],
          wrappers: ['panel-wrapper'],
          props: {
            hideHeader: true,
            required: false,
            label: 'Configure Location Levels Based on Conditions',
            collapsible: true,
            collapsed: true,
          },
          fieldGroup: [
            {
              key: 'configurationList',
              type: EIremboFormlyFieldTypes.customrepeater,
              className: 'form-builder-display-rules',
              props: {
                hideHeader: true,
                required: false,
                addText: 'Add Configuration',
                dragDisabled: true,
              },
              fieldArray: {
                key: 'conditionalConfigsList',
                className: 'form-builder-display-rules-group',
                type: EIremboFormlyFieldTypes['formly-group'],
                props: {
                  hideHeader: true,
                  placeholder: 'Configuration List',
                  required: false,
                  dragDisabled: true,
                },
                fieldGroup: [
                  {
                    key: 'locationFirstLevel',
                    type: EIremboFormlyFieldTypes.select,
                    props: {
                      placeholder: 'Select first level',
                      label: 'Location First Level',
                      required: true,
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: getLocationFieldLevelFieldOptions(false),
                    },
                    hooks: {
                      onInit: field => {
                        return field?.options?.fieldChanges?.pipe(
                          filter(e => {
                            return (
                              e.type === 'valueChanges' &&
                              e.field.parent?.key === field.parent?.key &&
                              e.field.key === field.key
                            );
                          }),
                          tap(() => {
                            field?.parent?.formControl
                              ?.get('locationLastLevel')
                              ?.setValue(undefined);
                          })
                        );
                      },
                    },
                  },
                  {
                    key: 'locationLastLevel',
                    type: EIremboFormlyFieldTypes.select,
                    props: {
                      placeholder: 'Select last level',
                      label: 'Location Last Level',
                      required: false,
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: [],
                    },
                    expressions: {
                      'props.options': field => {
                        const selectedFirst = field.model?.locationFirstLevel;
                        const levels = getLocationFieldLevelFieldOptions(false);
                        const startIndex = levels.findIndex(
                          x => x.value === selectedFirst
                        );

                        return startIndex !== -1 &&
                          startIndex < levels.length - 1
                          ? levels.slice(startIndex + 1)
                          : [];
                      },
                      hide: field => {
                        return (
                          field.props?.options &&
                          Array.isArray(field.props?.options) &&
                          field.props.options.length == 0
                        );
                      },
                      'props.required': '!field.hide',
                    },
                  },
                  {
                    key: 'conditions',
                    type: EIremboFormlyFieldTypes.customrepeater,
                    className: 'form-builder-display-rules',
                    props: {
                      hideHeader: true,
                      required: true,
                      addText: 'Add Condition',
                      dragDisabled: true,
                    },
                    fieldArray: {
                      key: 'conditionList',
                      className: 'form-builder-display-rules-group',
                      type: EIremboFormlyFieldTypes['formly-group'],
                      props: {
                        hideHeader: true,
                        placeholder: 'Conditions List',
                        required: false,
                        dragDisabled: true,
                      },
                      fieldGroup: [
                        {
                          key: 'operator',
                          type: EIremboFormlyFieldTypes.customdropdown,
                          props: {
                            hideHeader: true,
                            placeholder: 'AND or OR',
                            required: false,
                            options: GetEnumToLabelAndValueOptions(
                              EFormBuilderDisplayConditionComparator
                            ),
                            bindLabel: 'label',
                            bindValue: 'value',
                          },
                          expressions: {
                            'props.required': (field: FormlyFieldConfig) => {
                              return field?.parent?.key !== '0';
                            },
                            hide: (field: FormlyFieldConfig) => {
                              return field?.parent?.key === '0';
                            },
                          },
                        },
                        {
                          key: 'formField',
                          type: EIremboFormlyFieldTypes.select,
                          className: 'show-label',
                          props: {
                            label: 'Field to campare against',
                            required: true,
                            placeholder: 'Select a field to compare against',
                            bindLabel: 'label',
                            bindValue: 'value',
                            options: getCurrentServiceFormFields(
                              formBuilderServiceInstance
                            ),
                            compareWith: (
                              o1: IBasicLabelValuePair<TFormlyFieldKey>,
                              o2: IBasicLabelValuePair<TFormlyFieldKey>
                            ) => {
                              return o1?.value === o2?.value;
                            },
                            change: (field: FormlyFieldConfig) => {
                              const form = field.form;
                              form?.get('formFieldProperty')?.reset();
                              form?.get('comparator')?.reset();
                              form?.get('fieldvalue')?.reset();
                            },
                          },
                        },
                        {
                          key: 'formFieldProperty',
                          type: EIremboFormlyFieldTypes.customdropdown,
                          props: {
                            label:
                              'Field setting or property to campare against',
                            placeholder:
                              'Select field setting/property to campare against',
                            bindLabel: 'label',
                            bindValue: '',
                            options: [
                              {
                                value: 'fieldvalue',
                                label: 'Field value',
                                type: 'unknown',
                              },
                              {
                                value: 'disabled',
                                label: 'Disabled',
                                type: 'boolean',
                              },
                              {
                                value: 'readonly',
                                label: 'Readonly',
                                type: 'boolean',
                              },
                              {
                                value: 'validity',
                                label: "Field's Validity",
                                type: 'boolean',
                              },
                            ],
                          },
                          expressions: {
                            hide: (field: FormlyFieldConfig) => {
                              return (
                                field.model?.formField?.value === 'formState'
                              );
                            },
                          },
                        },
                        {
                          key: 'formStateValue',
                          type: EIremboFormlyFieldTypes['formly-group'],
                          props: {
                            hideHeader: true,
                            required: false,
                          },
                          fieldGroup: [
                            {
                              key: 'formStateKey',
                              type: EIremboFormlyFieldTypes.select,
                              props: {
                                hideHeader: true,
                                placeholder: 'Select form State Key',
                                required: true,
                                bindValue: 'value',
                                options: [
                                  {
                                    label: 'Fetched Data',
                                    value:
                                      EIremboFormlyFormStateKeys.FETCHED_DATA,
                                  },
                                  {
                                    label: 'Application State',
                                    value:
                                      EIremboFormlyFormStateKeys.APPLICATION_STATE,
                                  },
                                  {
                                    label: 'Applicant Type',
                                    value:
                                      EIremboFormlyFormStateKeys.APPLICANT_TYPE,
                                  },
                                  {
                                    label: 'Application Locale',
                                    value: EIremboFormlyFormStateKeys.LOCALE,
                                  },
                                ],
                              },
                              expressions: {
                                'props.description': (
                                  field: FormlyFieldConfig
                                ) => {
                                  let description: string | undefined =
                                    undefined;
                                  switch (field?.formControl?.value) {
                                    case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID: {
                                      description =
                                        'Stores the business ID for business applicants';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_NAME: {
                                      description =
                                        'The business name for business applicants';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.APPLICANT_TYPE: {
                                      description = 'The applicant type';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.APPLICATION_STATE: {
                                      description = 'The application state';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.FETCHED_DATA: {
                                      description =
                                        'Fetched data received by fields that fetch external data against thier field key';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA: {
                                      description =
                                        'Fetched list of fetched options received by custom dropdowns with data fetch';
                                      break;
                                    }
                                    case EIremboFormlyFormStateKeys.LOCALE: {
                                      description =
                                        'The locale used in the application';
                                      break;
                                    }
                                    default:
                                      description = '';
                                  }
                                  return description;
                                },
                              },
                            },
                            {
                              key: 'fieldKey',
                              type: EIremboFormlyFieldTypes.customdropdown,
                              props: {
                                hideHeader: true,
                                placeholder:
                                  'Select the field key in the formState data',
                                required: true,
                                bindLabel: 'label',
                                bindValue: 'value',
                                options:
                                  fetchedDataFieldsFormFieldLabelAndDetails(
                                    formBuilderServiceInstance
                                  ),
                              },
                              expressions: {
                                hide: (field: FormlyFieldConfig) => {
                                  return !(
                                    field?.parent?.parent?.model?.formStateValue
                                      ?.formStateKey ===
                                    EIremboFormlyFormStateKeys.FETCHED_DATA
                                  );
                                },
                              },
                            },
                            {
                              key: 'valuePath',
                              type: EIremboFormlyFieldTypes.input,
                              props: {
                                hideHeader: true,
                                placeholder: 'Value path',
                                required: true,
                                description:
                                  'value path to access value e.g `sex` in {sex: "MALE"} or `users?.[0].sex` in  {users: [sex: "MALE"]}',
                              },
                              expressions: {},
                            },
                          ],
                          expressions: {
                            hide: (field: FormlyFieldConfig) => {
                              return (
                                field?.parent?.model?.formField?.value !==
                                'formState'
                              );
                            },
                            'props.required': '!field.hide',
                          },
                        },

                        {
                          key: 'comparator',
                          type: EIremboFormlyFieldTypes.customdropdown,
                          props: {
                            label: 'Comparator',
                            placeholder: 'Select a comparator',
                            bindLabel: 'label',
                            bindValue: 'value',
                            options: ServiceComparatorOptions,
                          },
                          expressions: {
                            'props.options': buildComparatorOptions,
                            'model.comparator': 'model?.comparator',
                            'props.required': '!field.hide',
                            hide: (field: FormlyFieldConfig) => {
                              if (
                                field?.model?.formField?.value === 'formState'
                              )
                                return false;

                              return (
                                field?.model?.formFieldProperty?.type ===
                                  'boolean' ||
                                ['checkbox'].includes(
                                  field?.model?.formField?.fieldType
                                )
                              );
                            },
                          },
                        },
                        {
                          key: 'fieldvalue',
                          type: EIremboFormlyFieldTypes.input,
                          props: {
                            label: 'Field value',
                            required: false,
                            placeholder: 'Enter field value',
                          },
                          validation: {
                            messages: {
                              required: 'This field is required',
                            },
                          },
                          expressions: {
                            hide: (field: FormlyFieldConfig) => {
                              if (
                                field.model?.formFieldProperty?.type ==
                                  'boolean' ||
                                field.model?.comparator === 'BOOLEAN'
                              ) {
                                return true;
                              }

                              if (
                                field.model?.formField?.value == 'formState'
                              ) {
                                return false;
                              }

                              return (
                                FormBuilderFieldsWithOptions.includes(
                                  field?.model?.formField?.fieldType
                                ) ||
                                FormBuilderFieldsWithDataSet.includes(
                                  field?.model?.formField?.fieldType
                                )
                              );
                            },
                            'props.required': '!field.hide',
                          },
                        },
                        {
                          key: 'fieldvalue',
                          type: EIremboFormlyFieldTypes.customdropdown,
                          props: {
                            label: 'Select option',
                            required: false,
                            placeholder: 'Select option',
                            bindLabel: 'label',
                            bindValue: 'value',
                            options: [
                              {
                                value: true,
                                label: 'True',
                              },
                              {
                                value: false,
                                label: 'False',
                              },
                            ],
                          },
                          validation: {
                            messages: {
                              required: 'This field is required',
                            },
                          },
                          expressions: {
                            hide: (field: FormlyFieldConfig) => {
                              if (
                                field.model?.formField?.value === 'formState' &&
                                field.model?.comparator !== 'BOOLEAN'
                              ) {
                                return true;
                              }

                              if (
                                field.model?.formFieldProperty?.type ===
                                  'boolean' ||
                                ['checkbox'].includes(
                                  field?.model?.formField?.fieldType
                                ) ||
                                field.model?.comparator === 'BOOLEAN'
                              ) {
                                return false;
                              }

                              return true;
                            },
                            'props.required': '!field.hide',
                            'props.options': (field: FormlyFieldConfig) => {
                              return field.model?.formFieldProperty?.value ===
                                'validity'
                                ? [
                                    {
                                      value: true,
                                      label: 'Is valid',
                                    },
                                    {
                                      value: false,
                                      label: 'Not vaild',
                                    },
                                  ]
                                : [
                                    {
                                      value: true,
                                      label: 'True',
                                    },
                                    {
                                      value: false,
                                      label: 'False',
                                    },
                                  ];
                            },
                          },
                        },
                        {
                          key: 'fieldvalue',
                          type: EIremboFormlyFieldTypes.customdropdown,
                          props: {
                            placeholder: 'Select a value',
                            label: 'Select a value',
                            options: [],
                          },
                          expressions: {
                            hide: (field: FormlyFieldConfig) => {
                              const { formField } = field?.model || {};
                              const { fieldType } = formField || {};

                              if (
                                field?.model?.formFieldProperty?.type ===
                                  'boolean' ||
                                field.model?.formField?.value == 'formState' ||
                                ['checkbox'].includes(
                                  field?.model?.formField?.fieldType
                                )
                              ) {
                                return true;
                              }

                              if (
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                              ) {
                                return true;
                              }
                              if (
                                fieldType ===
                                  EIremboFormlyFieldTypes.customdropdownpaginated ||
                                [
                                  EIremboFormlyFieldTypes.customdropdown,
                                  EIremboFormlyFieldTypes.radio,
                                  EIremboFormlyFieldTypes.multicheckbox,
                                  EIremboFormlyFieldTypes.select,
                                ].includes(fieldType)
                              ) {
                                return false;
                              }

                              return true;
                            },
                            'props.required': '!field?.hide',
                            'props.bindLabel': (field: FormlyFieldConfig) =>
                              formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                {
                                  compareField: field?.model?.formField,
                                }
                              )?.props?.['bindLabel'] ?? 'label',

                            'props.bindValue': (field: FormlyFieldConfig) =>
                              formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                {
                                  compareField: field?.model?.formField,
                                }
                              )?.props?.['bindValue'] ?? 'value',
                            'props.options': (field: FormlyFieldConfig) => {
                              let options: any = [];
                              if (
                                FormBuilderFieldsWithOptions.includes(
                                  field?.model?.formField?.fieldType
                                ) &&
                                field?.model?.formField?.fieldSubType !==
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH &&
                                field?.model?.formField?.fieldSubType !==
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                              ) {
                                options =
                                  formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                    {
                                      compareField: field?.model?.formField,
                                    }
                                  )?.props?.options;
                                return options;
                              }
                              return options;
                            },
                          },
                        },
                        {
                          key: 'fieldvalue',
                          type: 'input',
                          props: {
                            placeholder: 'Select a value',
                            label: 'Select a value',
                            options: [],
                            useBaseUrl: (field: FormlyFieldConfig) =>
                              field?.model?.formField?.fieldType ===
                                EIremboFormlyFieldTypes.customdropdownpaginated ||
                              field?.model?.formField?.fieldSubType ===
                                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                              field?.model?.formField?.fieldSubType ===
                                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
                            subType: (field: FormlyFieldConfig) =>
                              field?.model?.formField?.fieldSubType,
                          },
                          expressions: {
                            type: (field: FormlyFieldConfig) => {
                              if (
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                              ) {
                                return EIremboFormlyFieldTypes.customdropdown;
                              }
                              if (
                                field?.model?.formField?.fieldType ===
                                EIremboFormlyFieldTypes.customdropdownpaginated
                              ) {
                                return EIremboFormlyFieldTypes.customdropdownpaginated;
                              }
                              return EIremboFormlyFieldTypes.customdropdown;
                            },
                            hide: (field: FormlyFieldConfig) => {
                              if (
                                field?.model?.formFieldProperty?.type ===
                                  'boolean' ||
                                field.model?.formField?.value == 'formState' ||
                                ['checkbox'].includes(
                                  field?.model?.formField?.fieldType
                                )
                              ) {
                                return true;
                              }

                              if (
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                                field?.model?.formField?.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                              ) {
                                return false;
                              } else {
                                return true;
                              }
                            },
                            'props.required': '!field?.hide',
                            'props.dataset': (field: FormlyFieldConfig) => {
                              const { formField } = field?.model || {};
                              if (!formField) return;

                              if (
                                formField.fieldType ===
                                  EIremboFormlyFieldTypes.customdropdownpaginated ||
                                formField.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                                formField.fieldSubType ===
                                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                              ) {
                                return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                  { compareField: field?.model?.formField }
                                )?.props?.['dataset'];
                              }
                            },

                            'props.bindLabel': (field: FormlyFieldConfig) =>
                              formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                {
                                  compareField: field?.model?.formField,
                                }
                              )?.props?.['bindLabel'] ?? 'label',

                            'props.bindValue': (field: FormlyFieldConfig) =>
                              formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                {
                                  compareField: field?.model?.formField,
                                }
                              )?.props?.['bindValue'] ?? 'value',

                            'props.code': (field: FormlyFieldConfig) => {
                              if (
                                field?.model?.formField?.fieldSubType ===
                                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
                              ) {
                                return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                                  { compareField: field?.model?.formField }
                                )?.props?.['code'];
                              }
                            },
                          },
                        },
                        {
                          key: 'valueAsNumber',
                          type: EIremboFormlyFieldTypes.checkbox,
                          defaultValue: false,
                          props: {
                            hideHeader: true,
                            label: 'Value as Number',
                          },
                          expressions: {
                            hide: (field: FormlyFieldConfig) =>
                              field?.parent?.model?.formField?.value !==
                                'formState' ||
                              field.model?.comparator === 'BOOLEAN',
                          },
                        },
                      ],
                    },
                    expressions: {
                      'props.required': '!field.hide',
                    },
                    validation: {
                      messages: {
                        required: 'Condition is required',
                      },
                    },
                  },
                ],
              },
            },
          ],
          expressions: {},
        },
      ],
    },
  ];
};

export const locationLevelOptionsParseIntoSettingsFormModel = (
  field: FormlyFieldConfig,
  model: ILocationWidgetConfigurationFormModel
): ILocationWidgetConfigurationFormModel => {
  const locationFirstLevel: string =
    field?.props?.['locationFirstLevel'] ??
    Object.keys(LOCATION_WIDGET_LEVELS).shift();
  const locationLastLevel: string | undefined =
    field?.props?.['locationLastLevel'] ??
    (locationFirstLevel === LOCATION_COUNTRY_KEY
      ? undefined
      : Object.keys(LOCATION_WIDGET_LEVELS).pop());
  const enableInternationalCity: boolean =
    field?.props?.['enableInternationalCity'] ?? false;

  const excludedItemsConfiguration =
    buildExcludeItemsConfigurationFromFormConfigs(field);

  const defaultValues = buildDefaultValuesConfigurationFromFormConfigs(field);
  const conditionalLevelSettings = field?.props?.['conditionalLevelSettings'];

  model.locationFieldContentConfig = {
    international: {
      enableInternationalCity,
    },
    locationFirstLevel,
    locationLastLevel,
    excludedItemsConfiguration,
    defaultValues,
    conditionalLevelSettings,
  };
  return model;
};

export const locationLevelFieldUpdateFromSettingsFormModel = (
  model: ILocationWidgetConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig[] => {
  field.form?.reset();

  const dataseKeyOptions: string[] = Object.keys(LOCATION_WIDGET_LEVELS);
  const enableInternationalCity =
    model?.locationFieldContentConfig?.international?.enableInternationalCity ??
    false;

  const locationFirstLevel: string | undefined =
    model?.locationFieldContentConfig?.locationFirstLevel ??
    [...dataseKeyOptions].shift();

  const selectedLastLevel =
    model?.locationFieldContentConfig?.locationLastLevel;
  const locationLastLevel: string | undefined =
    selectedLastLevel ??
    (locationFirstLevel === LOCATION_COUNTRY_KEY
      ? undefined
      : [...dataseKeyOptions].pop());

  const countryFieldKey: string = `Country${alphaNanoIdUtil(5)}`;

  const parentFieldLabel: string = (field.props?.label ?? 'Location')
    .split(`: ${LOCATION_WIDGET_LOCAL_LABEL}`)
    .join('')
    .split(`: ${LOCATION_WIDGET_COUNTRY_LABEL}`)
    .join('')
    .split(`: ${LOCATION_WIDGET_CITY_LABEL}`)
    .join('');

  if (
    locationFirstLevel?.toLowerCase() === LOCATION_COUNTRY_KEY.toLowerCase()
  ) {
    const existingCountryFieldKey = field.props?.['countryFieldKey'] as
      | string
      | undefined;
    const countryRootKey =
      existingCountryFieldKey ??
      (field.props?.['locationWidgetRoot']
        ? (field.key as string)
        : undefined) ??
      countryFieldKey;
    let countryField: FormlyFieldConfig =
      LocationWidgetUtils.buildWidgetCountryField(
        countryRootKey,
        `${parentFieldLabel}: ${LOCATION_WIDGET_COUNTRY_LABEL}`,
        locationFirstLevel,
        countryRootKey
      );
    countryField = addExcludeConfigsToCountryField(
      countryField,
      model.locationFieldContentConfig?.excludedItemsConfiguration
    );

    const generatedFields: FormlyFieldConfig[] = [countryField];
    let locationCascadeField: FormlyFieldConfig | undefined;
    let cityField: FormlyFieldConfig | undefined;

    if (locationLastLevel) {
      const existingLocalFieldKey =
        (field.props?.['localLocationFieldKey'] as string | undefined) ??
        (field.type === EIremboFormlyFieldTypes.customcascadingdropdowns
          ? (field.key as string)
          : undefined);
      locationCascadeField = LocationWidgetUtils.buildCascadeField(
        countryRootKey,
        <string>locationFirstLevel,
        locationLastLevel,
        `${parentFieldLabel}: ${LOCATION_WIDGET_LOCAL_LABEL}`,
        undefined,
        false,
        existingLocalFieldKey
      );
      locationCascadeField = addExcludeConfigsToCascadingField(
        locationCascadeField,
        model.locationFieldContentConfig?.excludedItemsConfiguration
      );
      generatedFields.push(locationCascadeField);
    }

    if (enableInternationalCity) {
      cityField = LocationWidgetUtils.buildWidgetCityField(
        countryRootKey,
        <string>locationFirstLevel,
        true,
        `${parentFieldLabel}: ${LOCATION_WIDGET_CITY_LABEL}`,
        field.props?.['cityFieldKey'] as string | undefined
      );
      generatedFields.push(cityField);
    }

    if (countryField.props) {
      countryField.props['locationWidgetRoot'] = true;
      countryField.props['locationFirstLevel'] = locationFirstLevel;
      countryField.props['locationLastLevel'] = locationLastLevel;
      countryField.props['enableInternationalCity'] = enableInternationalCity;
      countryField.props['countryFieldKey'] = countryField.key;
      countryField.props['localLocationFieldKey'] = locationCascadeField?.key;
      countryField.props['cityFieldKey'] = cityField?.key;
    }
    if (locationCascadeField?.props) {
      locationCascadeField.props['locationFirstLevel'] = locationFirstLevel;
      locationCascadeField.props['locationLastLevel'] = locationLastLevel;
      locationCascadeField.props['enableInternationalCity'] =
        enableInternationalCity;
      locationCascadeField.props['countryFieldKey'] = countryField.key;
      locationCascadeField.props['cityFieldKey'] = cityField?.key;
      locationCascadeField.props['locationWidgetKey'] = countryField.key;
    }
    if (cityField?.props) {
      cityField.props['locationWidgetKey'] = countryField.key;
    }

    // The country field no longer always carries an `expressions` object (a
    // constant `hide` is not emitted anymore), so create it when absent instead
    // of silently skipping the required-expression wiring.
    countryField.expressions = {
      ...countryField.expressions,
      'props.required': `field.props?.countryRequired || field.props?.required`,
    };

    if (
      model.locationFieldContentConfig?.defaultValues?.locationWidget &&
      Object.keys(
        model.locationFieldContentConfig?.defaultValues?.locationWidget
      ).length > 0
    ) {
      if (locationCascadeField) {
        locationCascadeField.defaultValue = formatLocationWidgetDefaultValue(
          model,
          locationCascadeField
        );
      }
    }

    if (model.locationFieldContentConfig?.defaultValues?.COUNTRY) {
      countryField.defaultValue =
        model?.locationFieldContentConfig?.defaultValues?.COUNTRY;
    }

    if (model.locationFieldContentConfig?.defaultValues?.internationalCity) {
      if (cityField) {
        cityField.defaultValue =
          model?.locationFieldContentConfig?.defaultValues?.internationalCity;
      }
    }

    if (locationCascadeField) {
      const configuredCascade = addConditionalLevelSettings(
        model,
        locationCascadeField
      );
      const cascadeIndex = generatedFields.indexOf(locationCascadeField);
      generatedFields[cascadeIndex] = configuredCascade;
    }

    return generatedFields.map(generatedField =>
      preserveExistingLocationFieldConfiguration(generatedField, field)
    );
  }

  let locationCascadeFieldOnly: FormlyFieldConfig =
    LocationWidgetUtils.buildCascadeField(
      countryFieldKey,
      <string>locationFirstLevel,
      <string>locationLastLevel,
      parentFieldLabel,
      undefined,
      undefined,
      field?.key as string
    );

  if (locationCascadeFieldOnly.props) {
    locationCascadeFieldOnly.props['locationFirstLevel'] = locationFirstLevel;
    locationCascadeFieldOnly.props['locationLastLevel'] = locationLastLevel;
    locationCascadeFieldOnly.props['enableInternationalCity'] =
      enableInternationalCity;
    locationCascadeFieldOnly.props.label = locationCascadeFieldOnly.props?.label
      ?.split(`: ${LOCATION_WIDGET_LOCAL_LABEL}`)
      .join('');

    locationCascadeFieldOnly = addExcludeConfigsToCascadingField(
      locationCascadeFieldOnly,
      model.locationFieldContentConfig?.excludedItemsConfiguration
    );
  }

  locationCascadeFieldOnly = addDefaultValueSettings(
    model,
    locationCascadeFieldOnly
  );

  locationCascadeFieldOnly = addConditionalLevelSettings(
    model,
    locationCascadeFieldOnly
  );

  return [locationCascadeFieldOnly];
};

const preserveExistingLocationFieldConfiguration = (
  generatedField: FormlyFieldConfig,
  editorField: FormlyFieldConfig
): FormlyFieldConfig => {
  const existingField = editorField.parent?.fieldGroup?.find(
    candidate => candidate.key === generatedField.key
  );
  if (!existingField) return generatedField;

  const preservedPropKeys = [
    'label',
    'placeholder',
    'hint',
    'tooltip',
    'internalDescription',
    'internal',
    'readonly',
    'disabled',
    'hideHeader',
  ];
  const props = { ...generatedField.props };
  preservedPropKeys.forEach(key => {
    if (existingField.props && key in existingField.props) {
      props[key] = existingField.props[key];
    }
  });
  const expressions = {
    ...generatedField.expressions,
    ...existingField.expressions,
  };
  if (generatedField.expressions?.['props.required'] !== undefined) {
    expressions['props.required'] =
      generatedField.expressions['props.required'];
  }
  // A constant `hide: 'false'` is not a condition, but the builder flags any
  // field with a `hide` expression as conditionally hidden. Drop it so
  // re-saving a widget generated before this fix clears the stale flag rather
  // than carrying it forward.
  if (
    typeof expressions['hide'] === 'string' &&
    expressions['hide'].trim() === 'false'
  ) {
    delete expressions['hide'];
  }

  return {
    ...existingField,
    ...generatedField,
    id: existingField.id ?? generatedField.id,
    props,
    expressions,
    validation: existingField.validation ?? generatedField.validation,
    validators: existingField.validators ?? generatedField.validators,
    asyncValidators:
      existingField.asyncValidators ?? generatedField.asyncValidators,
  };
};

const validateLocationFieldLevelFieldValuesIndex = (
  firstLevelValue: string,
  lastLevelValue: string
): boolean => {
  const datasetOptions: string[] = Object.keys(LOCATION_WIDGET_LEVELS);
  return (
    datasetOptions.indexOf(firstLevelValue) <
    datasetOptions.indexOf(lastLevelValue)
  );
};

export const getLocationFieldLevelFieldOptions = (
  includeCountry: boolean = false
): IBasicLabelValuePair<string>[] => {
  const fieldOptions: IBasicLabelValuePair<string>[] = Object.keys(
    LOCATION_WIDGET_LEVELS
  ).map((key: string) => {
    return {
      label: LOCATION_WIDGET_LEVELS[key],
      value: key,
    };
  });

  if (includeCountry) {
    fieldOptions.unshift({
      label: 'Country',
      value: LOCATION_COUNTRY_KEY,
    });
  }
  return fieldOptions;
};

const generateDatasetExcludeConfigurationFields = () => {
  const levels: string[] = [
    LOCATION_COUNTRY_KEY,
    ...Object.keys(LOCATION_WIDGET_LEVELS),
  ];

  const fields: FormlyFieldConfig[] = [];

  levels.forEach(level => {
    const formattedLabel = `${level.charAt(0).toUpperCase()}${level
      .slice(1)
      .toLowerCase()}`;
    fields.push(
      {
        key: level,
        type: 'customdropdownpaginated',
        props: {
          code: `${level}`,
          hint: '',
          label: `${formattedLabel}`,
          dataset: {
            url: `/admin/v1/dataset-items/by-dataset-code/${level}/paginated`,
            dataField: 'data.content',
            pagination: 'true',
            searching: 'false',
            params: { size: 10, page: 0 },
          },
          options: [],
          subType: 'CUSTOM_DROPDOWN_DATASET_PAGINATED',
          tooltip: '',
          disabled: false,
          multiple: true,
          readonly: false,
          required: false,
          bindLabel: 'label',
          bindValue: '',
          useBaseUrl: true,
          placeholder: 'Select dataset items to exclude',
          internalDescription: '',
        },
        defaultValue: null,
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const startIndex = levels.indexOf(
              field?.parent?.parent?.model?.['locationFirstLevel']
            );
            const lastIndex = levels.indexOf(
              field?.parent?.parent?.model?.['locationLastLevel'] ??
                field?.parent?.parent?.model?.['locationFirstLevel']
            );

            let currentFieldIndex = -1;
            if (field?.key && typeof field?.key == 'string') {
              currentFieldIndex = levels.indexOf(field.key);
            }
            return !(
              currentFieldIndex >= startIndex && currentFieldIndex <= lastIndex
            );
          },
        },
        validation: {
          messages: {},
        },
      },
      {
        key: `${level}-excludedItemKey`,
        props: {},
        defaultValue: 'value',
        validation: {
          messages: {},
        },
      }
    );
  });
  return fields;
};

const updateConfigsWithExclude = (
  data: Record<string, string | Record<string, unknown>[]>,
  configs: ICascadingConfig[]
) => {
  return configs.map(config => {
    const matchedKey = Object.keys(data).find(key =>
      config.key.startsWith(key)
    );
    if (matchedKey) {
      const excludedValues = data[matchedKey];
      if (Array.isArray(excludedValues) && excludedValues.length > 0) {
        return {
          ...config,
          excludedItems: excludedValues.map(item => item['value']),
          excludedItemsDetails: excludedValues.map(item => ({
            label: item['label'],
            value: item['value'],
          })),
          excludedItemKey: data[`${matchedKey}-excludedItemKey`] as string,
        };
      }
    }
    return config;
  });
};

const buildExcludeItemsConfigurationFromFormConfigs = (
  field: FormlyFieldConfig
) => {
  const configs = field.props?.['configs'] as ICascadingConfig[];
  const levels = [LOCATION_COUNTRY_KEY, ...Object.keys(LOCATION_WIDGET_LEVELS)];

  const data = levels.reduce(
    (data: Record<string, string | Record<string, unknown>[]>, level) => {
      const config = configs.find(cfg => cfg.key.startsWith(level));

      if (level === LOCATION_COUNTRY_KEY) {
        if (field.props) {
          const countryFieldKey = field?.props['countryFieldKey'];
          const countryFieldProps = field?.parent?.fieldGroup?.find(
            x => x.key == countryFieldKey
          )?.props;

          if (countryFieldProps) {
            const countryExcludedItemsDetails =
              countryFieldProps['excludedItemsDetails'];
            data[level] = countryExcludedItemsDetails;
          }
        }
      }

      if (config?.excludedItemsDetails !== undefined) {
        data[level] = config.excludedItemsDetails;
      }
      if (config?.excludedItemKey !== undefined) {
        const excludedItemKey = `${level}-excludedItemKey`;
        data[excludedItemKey] = config?.['excludedItemKey'];
      }
      return data;
    },
    {}
  );

  return data;
};

const addExcludeConfigsToCascadingField = (
  locationCascadeField: FormlyFieldConfig,
  excludedItemsConfiguration?: Record<
    string,
    string | Record<string, unknown>[]
  >
) => {
  if (locationCascadeField.props && excludedItemsConfiguration) {
    const existingConfigs: ICascadingConfig[] =
      locationCascadeField?.props?.['configs'];

    locationCascadeField.props['configs'] = updateConfigsWithExclude(
      excludedItemsConfiguration,
      existingConfigs
    );
  }
  return locationCascadeField;
};

const addExcludeConfigsToCountryField = (
  countryField: FormlyFieldConfig,
  excludedItemsConfiguration?: Record<
    string,
    string | Record<string, unknown>[]
  >
) => {
  if (countryField.props && excludedItemsConfiguration) {
    const countryExcludedItems =
      excludedItemsConfiguration[LOCATION_COUNTRY_KEY];
    if (Array.isArray(countryExcludedItems)) {
      const exclude = countryExcludedItems.map(x => x['label']);
      countryField.props = {
        ...countryField.props,
        dataset: {
          ...countryField.props['dataset'],
          params: { ...countryField.props['dataset']['params'], exclude },
        },
        excludedItemsDetails: countryExcludedItems.map(item => ({
          label: item['label'],
          value: item['value'],
        })),
      };
    }
  }
  return countryField;
};

const generateDefaultValueConfigurationFields = () => {
  const fields: FormlyFieldConfig[] = [
    {
      key: LOCATION_COUNTRY_KEY,
      type: 'customdropdownpaginated',
      props: {
        code: `${LOCATION_COUNTRY_KEY}`,
        hint: '',
        label: 'Country',
        dataset: {
          url: `/admin/v1/dataset-items/by-dataset-code/${LOCATION_COUNTRY_KEY}/paginated`,
          dataField: 'data.content',
          pagination: 'true',
          searching: 'false',
          params: { size: 10, page: 0 },
        },
        options: [],
        subType: 'CUSTOM_DROPDOWN_DATASET_PAGINATED',
        tooltip: '',
        disabled: false,
        multiple: false,
        readonly: false,
        required: false,
        bindLabel: 'label',
        bindValue: '',
        useBaseUrl: true,
        placeholder: 'Select default value(s)',
        internalDescription: '',
      },
      defaultValue: null,
      expressions: {
        hide: hideLocationWidgetConfigurationField,
        'props.dataset': (field: FormlyFieldConfig) => {
          const excludedItemsConfig =
            field?.parent?.parent?.model?.['excludedItemsConfiguration'];
          return {
            ...field?.props?.['dataset'],
            params: {
              ...field?.props?.['dataset']['params'],
              exclude: excludedItemsConfig[LOCATION_COUNTRY_KEY]?.map(
                (x: { label: any }) => x.label
              ),
            },
          };
        },
      },
      validation: {
        messages: {},
      },
    },
    {
      key: 'locationWidget',
      type: 'customcascadingdropdowns',
      props: {
        required: false,
        label: '',
        configs: [],
      },
      expressions: {
        'props.configs': buildLocationConfig,
      },
    },
    {
      key: 'internationalCity',
      id: 'internationalCity',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'City',
        disabled: false,
        required: false,
        placeholder: 'Enter default city',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return !field?.parent?.parent?.parent?.model
            ?.locationFieldContentConfig?.international
            ?.enableInternationalCity;
        },
      },
      validation: {
        messages: {
          required: 'Placeholder is required',
        },
      },
    },
  ];
  return fields;
};

const hideLocationWidgetConfigurationField = (field: FormlyFieldConfig) => {
  const levels: string[] = [
    LOCATION_COUNTRY_KEY,
    ...Object.keys(LOCATION_WIDGET_LEVELS),
  ];
  const startIndex = levels.indexOf(
    field?.parent?.parent?.model?.['locationFirstLevel']
  );
  const lastIndex = levels.indexOf(
    field?.parent?.parent?.model?.['locationLastLevel']
  );

  let currentFieldIndex = -1;
  if (field?.key && typeof field?.key == 'string') {
    currentFieldIndex = levels.indexOf(field.key);
  }
  return !(currentFieldIndex >= startIndex && currentFieldIndex <= lastIndex);
};

const buildLocationConfig = (field: FormlyFieldConfig) => {
  const configs: ICascadingConfig[] = [];
  const allLevels: string[] = [...Object.keys(LOCATION_WIDGET_LEVELS)];
  let startLevel = field?.parent?.parent?.model?.['locationFirstLevel'];
  startLevel = startLevel === LOCATION_COUNTRY_KEY ? 'PROVINCE' : startLevel;
  const lastLevel =
    field?.parent?.parent?.model?.['locationLastLevel'] ?? startLevel;
  const levels = filterLevels(allLevels, startLevel, lastLevel);

  const excludedItemsConfig =
    field?.parent?.parent?.model?.['excludedItemsConfiguration'];

  levels.forEach((level, index) => {
    const formattedLabel = formatLevel(level);
    if (index === 0) {
      configs.push({
        key: level,
        label: formattedLabel,
        parent: null,
        dataset: {
          url: `/admin/v1/dataset-items/by-dataset-code/${level}`,
          dataField: 'data',
          useBaseUrl: true,
        },
        required: false,
        bindlabel: 'label',
        bindvalue: '',
        placeholder: 'Select default value',
        excludedItemKey: 'value',
        excludedItems: excludedItemsConfig[level]?.map(
          (x: { value: any }) => x.value
        ),
      });
    } else {
      configs.push({
        key: level,
        label: formattedLabel,
        parent: levels[index - 1],
        dataset: {
          url: '/admin/v1/dataset-items/by-details',
          dataField: 'data',
          useBaseUrl: true,
        },
        required: false,
        bindlabel: 'label',
        bindvalue: '',
        placeholder: 'Select default values',
        parentBindKey: 'id',
        excludedItemKey: 'value',
        excludedItems: excludedItemsConfig[level]?.map(
          (x: { value: any }) => x.value
        ),
      });
    }
  });
  return configs;
};

const filterLevels = (
  allLevels: string[],
  startLevel: string,
  endLevel: string
) => {
  const startIndex = allLevels.indexOf(startLevel);
  const endIndex = allLevels.indexOf(endLevel);

  if (startIndex === -1 || endIndex === -1) {
    return [];
  }
  const lowerIndex = Math.min(startIndex, endIndex);
  const upperIndex = Math.max(startIndex, endIndex);

  return allLevels.slice(lowerIndex, upperIndex + 1);
};

const formatLevel = (level: string) => {
  return `${level.charAt(0).toUpperCase()}${level.slice(1).toLowerCase()}`;
};

const formatLocationWidgetDefaultValue = (
  model: ILocationWidgetConfigurationFormModel,
  field: FormlyFieldConfig
) => {
  const formattedDefaultValue: Record<string, unknown> = {};
  if (
    field.props &&
    model.locationFieldContentConfig?.defaultValues?.locationWidget
  ) {
    const configs: ICascadingConfig[] = field.props['configs'];
    const defaultValue = Object.values(
      model?.locationFieldContentConfig?.defaultValues?.locationWidget
    );
    if (Array.isArray(configs)) {
      configs.forEach((config, index) => {
        formattedDefaultValue[config.key] = defaultValue[index];
      });
    }
  }
  return formattedDefaultValue;
};

const buildDefaultValuesConfigurationFromFormConfigs = (
  field: FormlyFieldConfig
) => {
  const allLevels: string[] = [...Object.keys(LOCATION_WIDGET_LEVELS)];
  let startLevel = field?.props?.['locationFirstLevel'];
  startLevel = startLevel === LOCATION_COUNTRY_KEY ? 'PROVINCE' : startLevel;
  const lastLevel = field?.props?.['locationLastLevel'] ?? startLevel;
  const levels = filterLevels(allLevels, startLevel, lastLevel);

  const fieldGroup = field.parent?.fieldGroup;
  const response = {
    locationWidget: {},
    COUNTRY: {},
    internationalCity: '',
  };
  let locationWidgetDefaultValue: Record<string, unknown>[] = [];
  const formattedDefaultValue: Record<string, unknown> = {};

  if (field.props) {
    response.COUNTRY =
      fieldGroup?.find(x => x.key === field.props?.['countryFieldKey'])
        ?.defaultValue ?? {};
    response.internationalCity =
      fieldGroup?.find(x => x.key === field.props?.['cityFieldKey'])
        ?.defaultValue ?? '';
  }

  if (field.defaultValue) {
    locationWidgetDefaultValue = Object.values(field.defaultValue);
  }

  levels.forEach((level, index) => {
    formattedDefaultValue[level] = locationWidgetDefaultValue[index];
  });

  response.locationWidget = formattedDefaultValue;

  return response;
};

const addDefaultValueSettings = (
  model: ILocationWidgetConfigurationFormModel,
  locationCascadeFieldOnly: FormlyFieldConfig
) => {
  if (
    model.locationFieldContentConfig?.defaultValues?.locationWidget &&
    Object.keys(model.locationFieldContentConfig?.defaultValues?.locationWidget)
      .length > 0
  ) {
    locationCascadeFieldOnly.defaultValue = formatLocationWidgetDefaultValue(
      model,
      locationCascadeFieldOnly
    );
  }
  return locationCascadeFieldOnly;
};

const addConditionalLevelSettings = (
  model: ILocationWidgetConfigurationFormModel,
  locationCascadeFieldOnly: FormlyFieldConfig
) => {
  if (
    model.locationFieldContentConfig?.conditionalLevelSettings
      ?.configurationList &&
    model.locationFieldContentConfig.conditionalLevelSettings.configurationList
      .length > 0
  ) {
    if (locationCascadeFieldOnly.props) {
      locationCascadeFieldOnly.props['conditionalLevelSettings'] =
        model.locationFieldContentConfig.conditionalLevelSettings;
    }
  }

  return locationCascadeFieldOnly;
};
